import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { api } from "@/lib/api";
import { CheckCircle, XCircle, Spinner } from "@phosphor-icons/react";

/**
 * v3.2.31 · Stripe Checkout success landing. Polls the status endpoint
 * until Stripe/webhook confirms `paid` (or the poll budget expires),
 * then bounces back to the job page with a success flag so the
 * milestone board picks up the new `paid_at` stamp.
 */
export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const jobId     = params.get("job_id");
  const msIdx     = params.get("ms");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [error, setError]   = useState("");
  const [tries, setTries]   = useState(0);
  const MAX_TRIES = 20;   // 20 × 1.5s = 30s poll budget

  useEffect(() => {
    if (!sessionId) { setError("Missing session id."); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status === "paid") return;
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setError(`Payment ${data.payment_status}.`);
          return;
        }
        setTries((t) => {
          if (t + 1 >= MAX_TRIES) {
            setError("Payment is still processing. Refresh the job page in a moment.");
            return t + 1;
          }
          setTimeout(poll, 1500);
          return t + 1;
        });
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Could not verify payment.");
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const isPaid = status.payment_status === "paid";
  const backHref = jobId ? `/jobs/${jobId}#milestone-${(Number(msIdx) || 1) - 1}` : "/jobs";

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar />
      <div className="max-w-2xl mx-auto px-6 md:px-10 py-16">
        <div className="label-tag">// checkout</div>
        <h1 className="mt-2 font-display text-4xl md:text-5xl font-black tracking-tighter"
            data-testid="payment-success-heading">
          {isPaid ? "Payment received" : error ? "Payment status" : "Confirming…"}
        </h1>

        <div className="mt-8 border border-white/10 p-6 md:p-8"
             data-testid="payment-status-card">
          <div className="flex items-start gap-4">
            {isPaid ? (
              <CheckCircle size={40} weight="fill" className="text-emerald-400" />
            ) : error ? (
              <XCircle size={40} weight="fill" className="text-[#FF3B30]" />
            ) : (
              <Spinner size={40} className="text-[#00E5FF] animate-spin" />
            )}
            <div className="flex-1">
              {isPaid && (
                <>
                  <div className="text-white font-display text-2xl font-black" data-testid="payment-success-amount">
                    {status.currency} {Number(status.amount || 0).toLocaleString()}
                  </div>
                  <div className="text-gray-400 text-sm mt-1">
                    ≈ ${Number(status.amount_usd || 0).toLocaleString()} USD
                  </div>
                  {status.milestone_title && (
                    <div className="mt-3 text-sm text-gray-300">
                      MS{status.milestone_idx} · {status.milestone_title}
                    </div>
                  )}
                  <div className="mt-4 text-xs font-mono text-gray-500">
                    session · {status.session_id}
                  </div>
                </>
              )}
              {error && !isPaid && (
                <div className="text-[#FF3B30] text-sm" data-testid="payment-error">{error}</div>
              )}
              {!isPaid && !error && (
                <div className="text-gray-400 text-sm">
                  Waiting for Stripe to confirm the charge. This usually takes 2–3 seconds.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center gap-3">
          <button onClick={() => navigate(backHref)}
            className="btn-primary text-sm py-2"
            data-testid="payment-back-to-job-btn">
            Back to job →
          </button>
          <button onClick={() => navigate("/jobs")}
            className="btn-ghost text-sm py-2"
            data-testid="payment-back-to-jobs-btn">
            All jobs
          </button>
        </div>
      </div>
    </div>
  );
}
